import { coverUrl, pickInheritedImages, type SocialImage } from '../lib/socialImages';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Facebook, Instagram, Linkedin, Sparkles, Loader2, CheckCircle, AlertCircle, Link2 } from 'lucide-react';
import { FocusHeader } from '../components/FocusHeader';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { generateBlogUrl, getBlogUrlConfigForUser } from '../lib/blogUrlHelper';
import { extractPostContent } from '../lib/socialContent';

type Platform = 'linkedin' | 'facebook' | 'instagram';

interface Blog {
  id: string;
  title: string;
  intro: string;
  content: string;
  slug: string;
  featured_image_url: string | null;
  wp_post_url: string | null;
}

interface Webhook {
  platform: Platform;
  webhook_url: string;
  is_active: boolean;
}

export default function SocialMediaCreate() {
  const navigate = useNavigate();
  const { blogId } = useParams<{ blogId: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [blogId]);

  async function loadData() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: dbUser } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!dbUser) throw new Error('User not found');

      const [blogResult, userResult] = await Promise.all([
        supabase
          .from('published_blogs')
          .select('id, title, intro, content, slug, featured_image_url, wp_post_url')
          .eq('id', blogId)
          .single(),
        supabase
          .from('social_profiles')
          .select('connected_platforms')
          .eq('user_id', dbUser.id)
          .maybeSingle()
      ]);

      if (blogResult.error) throw blogResult.error;
      setBlog(blogResult.data);

      if (userResult.data?.connected_platforms) {
        const connected = userResult.data.connected_platforms as Record<
          string,
          { handle?: string; reauth_required?: boolean } | '' | undefined
        >;
        const webhooksArray: Webhook[] = [];

        for (const platform of ['instagram', 'linkedin', 'facebook'] as const) {
          const acct = connected[platform];
          if (acct && typeof acct === 'object' && acct.handle && !acct.reauth_required) {
            webhooksArray.push({
              platform,
              webhook_url: 'upload-post',
              is_active: true,
            });
          }
        }

        setWebhooks(webhooksArray);
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGeneratePost(platform: Platform) {
    if (!blog) return;

    setGenerating(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: dbUser } = await supabase
        .from('users')
        .select('id, website_url')
        .eq('auth_user_id', user.id)
        .single();

      if (!dbUser) throw new Error('User not found');

      const blogUrlConfig = await getBlogUrlConfigForUser(dbUser.id, supabase);
      // Eén bron van waarheid: de helper kiest zelf de eind-url (WordPress →
      // joepa → eigen domein → lees-nu) en filtert een lokale test-WordPress eruit.
      const blogUrl = generateBlogUrl(blog.slug, blogUrlConfig, blog.wp_post_url);

      const promptType = `social_media_${platform}`;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          promptType,
          variables: {
            title: blog.title,
            content: blog.content,
            website_url: dbUser.website_url || '',
            blog_url: blogUrl,
          },
        }),
      });

      const responseText = await response.text();
      console.log('Raw API response:', responseText.substring(0, 500));

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response was:', responseText);
        throw new Error('Ongeldige response van server. Probeer het opnieuw.');
      }

      if (!response.ok) {
        if (result.error === 'INSUFFICIENT_CREDITS') {
          throw new Error(result.message || 'Onvoldoende credits');
        }
        throw new Error(result.message || 'Failed to generate post');
      }

      // Reeks overnemen van een eerdere post uit dezelfde blog, zodat de klant
      // niet per kanaal opnieuw hoeft te uploaden. De verwijzingen gaan mee;
      // er wordt niets opnieuw geüpload.
      //
      // Eigen try/catch: dit is een gemak, geen kernfunctie, en de credit voor
      // de generatie is op dit punt al verbruikt. Een mislukte overname mag de
      // post dus nooit laten sneuvelen; dan valt hij terug op het losse beeld.
      //
      // limit(10): meer dan tien eerdere posts op dezelfde blog komt zelden
      // voor. Gebeurt het wel, en staat de laatste reeks verder terug, dan
      // wordt er niets overgenomen en gedraagt de post zich als vroeger.
      let overgenomen: SocialImage[] = [];
      try {
        const { data: eerdereRijen, error: overnameFout } = await supabase
          .from('social_media_posts')
          .select('images, created_at')
          .eq('user_id', dbUser.id)
          .eq('blog_id', blog.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (overnameFout) throw overnameFout;
        overgenomen = pickInheritedImages(eerdereRijen ?? []);
      } catch (err) {
        console.warn('Kon de beeldreeks niet overnemen:', err);
      }

      const { data: newPost, error: insertError } = await supabase
        .from('social_media_posts')
        .insert({
          user_id: dbUser.id,
          blog_id: blog.id,
          platform,
          post_content: extractPostContent(result.data),
          image_url: coverUrl(overgenomen, blog.featured_image_url),
          images: overgenomen.length > 0 ? overgenomen : null,
          status: 'draft',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      navigate(`/social-media/edit/${newPost.id}`);
    } catch (err: any) {
      console.error('Error generating post:', err);
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  const allPlatforms = [
    {
      id: 'linkedin' as Platform,
      name: 'LinkedIn',
      icon: Linkedin,
      accentColor: 'border-l-[#0A66C2]',
      description: 'Deel automatisch naar LinkedIn'
    },
    {
      id: 'facebook' as Platform,
      name: 'Facebook',
      icon: Facebook,
      accentColor: 'border-l-[#1877F2]',
      description: 'Deel automatisch naar Facebook'
    },
    {
      id: 'instagram' as Platform,
      name: 'Instagram',
      icon: Instagram,
      accentColor: 'border-l-[#E4405F]',
      description: 'Deel automatisch naar Instagram'
    },
  ];

  if (loading) {
    return (
      <>
        <FocusHeader
          title="Social Media post maken"
          onBack={() => navigate('/my-blogs')}
        />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (!blog) {
    return (
      <>
        <FocusHeader
          title="Social Media post maken"
          onBack={() => navigate('/my-blogs')}
        />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-600">Blog niet gevonden</p>
        </div>
      </>
    );
  }

  const hasAnyWebhook = webhooks.length > 0;

  return (
    <>
      <FocusHeader
        title="Social Media post maken"
        subtitle={blog.title}
        onBack={() => navigate('/my-blogs')}
      />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {!hasAnyWebhook && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Koppel eerst een platform
                </h3>
                <p className="text-gray-700">
                  Om social media posts te genereren moet je eerst een platform koppelen. Klik op een account om de koppeling te starten.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Kies een platform</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {allPlatforms.map((platform) => {
              const Icon = platform.icon;
              const isGenerating = generating && selectedPlatform === platform.id;
              const hasWebhook = webhooks.some(webhook => webhook.platform === platform.id && webhook.webhook_url);

              return (
                <div
                  key={platform.id}
                  className={`border-2 border-l-4 ${platform.accentColor} rounded-lg p-6 transition-all flex flex-col ${
                    hasWebhook
                      ? 'bg-white hover:shadow-md'
                      : 'bg-gradient-to-b from-orange-50/60 to-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <Icon className={`w-8 h-8 ${hasWebhook ? 'text-gray-700' : 'text-gray-500'}`} />
                    {hasWebhook ? (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Gekoppeld
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-orange-700 bg-orange-50 px-2.5 py-1 rounded-full">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Koppeling vereist
                      </div>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{platform.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {hasWebhook ? platform.description : 'Eenmalig koppelen, daarna automatisch'}
                  </p>

                  {hasWebhook ? (
                    <button
                      onClick={() => {
                        setSelectedPlatform(platform.id);
                        handleGeneratePost(platform.id);
                      }}
                      disabled={generating}
                      className="mt-auto w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#231F20] text-white rounded-lg hover:bg-[#231F20]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Post genereren...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Genereer Post
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/dashboard/settings?tab=socials')}
                      className="mt-auto w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Link2 className="w-4 h-4" />
                      Koppel {platform.name}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
